import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "address", "name", "lat", "lng", "street", "locality", "administrativeAreaLevel1", "administrativeAreaLevel2",
    "zipcode", "country", "googlePlaceId", "countryCode", "presets"
  ]

  connect() {
    this.autocomplete = new google.maps.places.Autocomplete(this.addressTarget)
    this.autocomplete.addListener("place_changed", this.locationChanged.bind(this))
    this.shouldShowPresets = this.addressTarget.value.trim().length === 0
    this.togglePresets()
  }

  preventSubmit(e) {
    if (e.key == "Enter") {
      e.preventDefault()
    }
  }

  fallbackToPreviousResult() {
    if (this.addressTarget.value.trim() === "") {
      this.clearLocationFields()
      return
    }

    this.addressTarget.value = this.currentPlaceAddress
    this.nameTarget.value = this.currentPlaceName
    this.togglePresets()
  }

  togglePresets() {
    if (!this.hasPresetsTarget) return

    this.presetsTarget.classList.toggle("d-flex", this.shouldShowPresets)
    this.presetsTarget.style.display = this.shouldShowPresets ? "" : "none"
  }

  selectPreset(event) {
    const { value, locality, country, countryCode } = event.params

    this.clearLocationFields()

    this.addressTarget.value = value || ""
    this.currentPlaceAddress = value || ""
    this.currentPlaceName = value || ""

    if (this.hasNameTarget) this.nameTarget.value = value || ""
    if (this.hasLocalityTarget) this.localityTarget.value = locality || ""
    if (this.hasCountryTarget) this.countryTarget.value = country || ""
    if (this.hasCountryCodeTarget) this.countryCodeTarget.value = countryCode || ""
  }

  clearLocationFields() {
    this.currentPlaceAddress = ""
    this.currentPlaceName = ""

    if (this.hasNameTarget) this.nameTarget.value = ""
    if (this.hasLocalityTarget) this.localityTarget.value = ""
    if (this.hasCountryTarget) this.countryTarget.value = ""
    if (this.hasCountryCodeTarget) this.countryCodeTarget.value = ""
    if (this.hasStreetTarget) this.streetTarget.value = ""
    if (this.hasAdministrativeAreaLevel1Target) this.administrativeAreaLevel1Target.value = ""
    if (this.hasAdministrativeAreaLevel2Target) this.administrativeAreaLevel2Target.value = ""
    if (this.hasZipcodeTarget) this.zipcodeTarget.value = ""
    if (this.hasGooglePlaceIdTarget) this.googlePlaceIdTarget.value = ""
    if (this.hasLatTarget) this.latTarget.value = ""
    if (this.hasLngTarget) this.lngTarget.value = ""
  }

  // Private

  locationChanged() {
    const place = this.autocomplete.getPlace()
    const isEstablishment = place.types?.includes("establishment")

    if (place.geometry) {
      this.currentPlaceAddress = place.formatted_address

      if (this.hasNameTarget && place.vicinity) {
        this.currentPlaceName = place.vicinity
      } else if (place.name) {
        this.currentPlaceName = place.name
      } else {
        this.currentPlaceName = place.formatted_address
      }

      // address is automatically set by google Autocomplete
      // we need to manually update hidden name input
      if (this.hasNameTarget) {
        this.nameTarget.value = this.currentPlaceName
      }

      if (this.hasStreetTarget) {
        const streetComponent = place.address_components.find(component => component.types.includes("street_address") || component.types.includes("route"))
        this.streetTarget.value = streetComponent ? streetComponent.long_name : ""
      }

      if (this.hasLocalityTarget) {
        const localityComponent = place.address_components.find(component => component.types.includes("locality") || component.types.includes("sublocality") || component.types.includes("neighborhood"))
        this.localityTarget.value = localityComponent ? localityComponent.long_name : ""
      }

      if (this.hasAdministrativeAreaLevel1Target) {
        const administrativeAreaLevel1Component = place.address_components.find(component => component.types.includes("administrative_area_level_1"))
        this.administrativeAreaLevel1Target.value = administrativeAreaLevel1Component ? administrativeAreaLevel1Component.long_name : ""
      }

      if (this.hasAdministrativeAreaLevel2Target) {
        const administrativeAreaLevel2Component = place.address_components.find(component => component.types.includes("administrative_area_level_2"))
        this.administrativeAreaLevel2Target.value = administrativeAreaLevel2Component ? administrativeAreaLevel2Component.long_name : ""
      }
      if (this.hasZipcodeTarget) {
        const zipcodeComponent = place.address_components.find(component => component.types.includes("postal_code"))
        this.zipcodeTarget.value = zipcodeComponent ? zipcodeComponent.long_name : ""
      }

      if (this.hasCountryTarget) {
        const countryComponent = place.address_components.find(component => component.types.includes("country"))
        this.countryTarget.value = countryComponent ? countryComponent.long_name : ""
        this.countryCodeTarget.value = countryComponent ? countryComponent.short_name : ""
      }

      if (this.hasGooglePlaceIdTarget) {
        this.googlePlaceIdTarget.value = isEstablishment ? place.place_id : ""
      }

      this.latTarget.value = place.geometry.location.lat()
      this.lngTarget.value = place.geometry.location.lng()
    }
  }

  // Getters

  get currentPlaceAddress() {
    return this.data.get("current-place-address")
  }

  get currentPlaceName() {
    return this.data.get("current-place-name")
  }

  // Setters

  set currentPlaceAddress(value) {
    this.data.set("current-place-address", value)
  }

  set currentPlaceName(value) {
    this.data.set("current-place-name", value)
  }
}
